// 2. External libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';

// 4. Type imports
import type { UseMutationResult } from '@tanstack/react-query';
import type { OnboardingStateResponse, UpdateOnboardingStateRequest } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { updateStateV1OnboardingStatePatch } from '~/generated/api/onboarding/onboarding';
import { queryKeys } from './queryKeys';

/**
 * Hook for updating onboarding wizard state in Clerk publicMetadata.
 *
 * Calls PATCH /v1/onboarding/state with authenticated request.
 * Backend merges updates into existing onboarding_progress field.
 *
 * Supports 6-step Telegram demo flow with fields for connection tracking
 * (telegramConnectionId, mcpRestConnectionId), client type selection, and
 * milestone completion flags (messageSent, messageRetrieved).
 *
 * Per §TS.5.1: Full generic type parameters for TanStack Query mutation.
 * Per §TS.3.1: Function declaration for named export.
 *
 * @returns UseMutationResult with mutate, mutateAsync, isPending, isSuccess, error, reset
 *
 * @example
 * ```tsx
 * const mutation = useUpdateOnboardingStateMutation();
 *
 * mutation.mutate({
 *   currentStep: 3,
 *   topicId: 'abc-123',
 *   telegramConnectionId: 'def-456',
 *   clientType: 'mcp',
 * });
 * ```
 */
export function useUpdateOnboardingStateMutation(): UseMutationResult<
  OnboardingStateResponse,
  Error,
  UpdateOnboardingStateRequest,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<OnboardingStateResponse, Error, UpdateOnboardingStateRequest, unknown>({
    mutationFn: async (data: UpdateOnboardingStateRequest) => {
      return updateStateV1OnboardingStatePatch(data);
    },
    onSuccess: () => {
      // Invalidate user query to refetch updated metadata
      void queryClient.invalidateQueries({ queryKey: queryKeys.user });
    },
    retry: 1,
  });
}
